import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowSquareOut, CaretLeft, CaretRight, DotsThree, FileText, Play } from '@phosphor-icons/react';
import type { BriefAsset, ClientReview, ClientVersion } from '@mybusinessmate/schema';
import skyBackground from '../../assets/sky-background.png';
import { assetUrlQuery } from '../../lib/asset-url';
import { captionOf } from '../../lib/client-review-display';
import { cx } from '../../lib/cx';
import { initialsOf } from '../../lib/initials';
import { Icon } from '../Icon';
import { ProgressBar } from '../ui/progress-bar';
import { AssetImage } from '../tasks/asset-image';

/** The sky behind whatever is being reviewed (Figma "Sky Background"), under a soft fade to the page. */
export function SkyStage({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        'relative flex min-h-(--mbm-size-review-stage) flex-col items-center justify-between gap-(--mbm-space-4) overflow-hidden rounded-(--mbm-component-radius-widget) bg-cover bg-center px-(--mbm-space-4) py-(--mbm-space-6)',
        className,
      )}
      style={{ backgroundImage: `url(${skyBackground})` }}
    >
      {children}
    </div>
  );
}

/** The post as Instagram will draw it: the account, the slides, the dots and the words under them. */
export function InstagramPreview({ review, version }: { review: ClientReview; version: ClientVersion }) {
  const [index, setIndex] = useState(0);
  const slides = version.assets;
  const slide = slides[Math.min(index, Math.max(slides.length - 1, 0))];
  const business = review.task.businessName ?? 'your business';
  const handle = business.toLowerCase().replace(/[^a-z0-9]/g, '');
  const caption = captionOf(version);

  return (
    <div className="flex w-full max-w-(--mbm-size-ig-preview) flex-col overflow-hidden rounded-(--mbm-radius-md) bg-surface-card shadow-(--mbm-shadow-md)">
      <div className="flex items-center gap-(--mbm-space-2-5) px-(--mbm-space-4) py-(--mbm-space-3)">
        <span className="flex size-(--mbm-size-avatar-md) shrink-0 items-center justify-center rounded-full bg-(image:--mbm-gradient-brand) text-label-small text-text-on-dark">
          {initialsOf(business)}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-label-medium text-text-primary">{handle}</span>
          <span className="text-caption text-text-tertiary">Preview · not posted yet</span>
        </span>
        <span className="text-text-secondary">
          <Icon icon={DotsThree} size="md" />
        </span>
      </div>
      <div className="relative aspect-square w-full bg-surface-field">
        {slide ? <AssetImage asset={slide} /> : null}
        {slides.length > 1 ? (
          <>
            <button
              type="button"
              aria-label="Previous image"
              disabled={index === 0}
              onClick={() => setIndex((i) => i - 1)}
              className="absolute left-(--mbm-space-2) top-1/2 flex size-(--mbm-space-7) -translate-y-1/2 items-center justify-center rounded-full bg-surface-card text-text-primary shadow-(--mbm-shadow-sm) disabled:hidden focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              <Icon icon={CaretLeft} size="sm" />
            </button>
            <button
              type="button"
              aria-label="Next image"
              disabled={index >= slides.length - 1}
              onClick={() => setIndex((i) => i + 1)}
              className="absolute right-(--mbm-space-2) top-1/2 flex size-(--mbm-space-7) -translate-y-1/2 items-center justify-center rounded-full bg-surface-card text-text-primary shadow-(--mbm-shadow-sm) disabled:hidden focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              <Icon icon={CaretRight} size="sm" />
            </button>
          </>
        ) : null}
      </div>
      {slides.length > 1 ? (
        <div className="flex justify-center gap-(--mbm-space-1) pt-(--mbm-space-3)" aria-hidden>
          {slides.map((s, i) => (
            <span
              key={s.id}
              className={cx(
                'size-(--mbm-space-1-5) rounded-full',
                i === index ? 'bg-brand-primary' : 'bg-border-default',
              )}
            />
          ))}
        </div>
      ) : null}
      <p className="whitespace-pre-wrap px-(--mbm-space-4) pb-(--mbm-space-5) pt-(--mbm-space-3) text-body-small text-text-primary">
        <span className="font-semibold">{handle}</span> {caption}
      </p>
    </div>
  );
}

/** A reel plays in a portrait frame: the file through a signed link, or a card for the link the team pasted. */
export function ReelPreview({ version }: { version: ClientVersion }) {
  const video = version.assets.find((a) => a.contentType.startsWith('video/'));
  const { data: url } = useQuery({ ...assetUrlQuery(video?.key ?? ''), enabled: !!video });
  const link = version.details.type === 'reel' ? version.details.videoUrl : null;
  return (
    <div className="relative aspect-[9/16] w-full max-w-(--mbm-size-reel-preview) overflow-hidden rounded-(--mbm-radius-md) bg-(image:--mbm-gradient-brand) shadow-(--mbm-shadow-md)">
      {url ? (
        <video src={url} controls playsInline preload="metadata" className="size-full bg-action-primary object-cover" />
      ) : (
        <span className="flex size-full flex-col items-center justify-center gap-(--mbm-space-3) text-text-on-dark">
          <span className="flex size-(--mbm-size-avatar-xl) items-center justify-center rounded-full bg-surface-card text-text-primary">
            <Icon icon={Play} size="lg" />
          </span>
          {link ? (
            <a href={link} target="_blank" rel="noreferrer noopener" className="text-label-large underline">
              Watch the reel
            </a>
          ) : null}
        </span>
      )}
    </div>
  );
}

/** A site build: where to look at it. It is a preview only; customers can't see it until the launch is approved. */
export function SitePreview({ version, note = true }: { version: ClientVersion; note?: boolean }) {
  const d = version.details;
  const url = d.type === 'website' ? d.liveUrl : null;
  const href = url ? (/^https?:\/\//.test(url) ? url : `https://${url}`) : undefined;
  return (
    <div className="flex w-full max-w-(--mbm-size-ig-preview) flex-col gap-(--mbm-space-3) rounded-(--mbm-radius-md) bg-surface-card p-(--mbm-space-5) shadow-(--mbm-shadow-md)">
      <span className="text-label-large text-text-primary">{url ?? 'Your site'}</span>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex h-(--mbm-size-button-compact) items-center justify-center gap-(--mbm-space-2) rounded-(--mbm-component-radius-button) bg-surface-field text-label-large text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={ArrowSquareOut} size="sm" />
          Open staging site
        </a>
      ) : null}
      {note ? (
        <p className="text-body-small text-text-tertiary">
          A preview only. Customers can’t see it until you approve the launch.
        </p>
      ) : null}
    </div>
  );
}

/** Anything else (a website update, a general task): the files, to open one by one. */
export function FilesPreview({ assets }: { assets: BriefAsset[] }) {
  return (
    <ul className="grid w-full max-w-(--mbm-size-ig-preview) grid-cols-2 gap-(--mbm-space-3)">
      {assets.map((asset) => (
        <li
          key={asset.id}
          className="relative flex aspect-square items-center justify-center overflow-hidden rounded-(--mbm-radius-md) bg-surface-card shadow-(--mbm-shadow-md)"
        >
          <AssetImage asset={asset} />
          <span className="absolute inset-x-0 bottom-0 truncate bg-overlay-scrim px-(--mbm-space-2) py-(--mbm-space-1) text-label-small text-text-on-dark">
            <Icon icon={FileText} size="xs" /> {asset.name}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** One version in the strip under the stage: the chosen one is bigger and ringed (Figma "Version Thumb"). */
function VersionThumb({
  version,
  selected,
  onSelect,
}: {
  version: ClientVersion;
  selected: boolean;
  onSelect: () => void;
}) {
  const first = version.assets[0];
  return (
    <button
      type="button"
      aria-label={`Version ${version.versionNumber}`}
      aria-pressed={selected}
      onClick={onSelect}
      className={cx(
        'relative overflow-hidden rounded-(--mbm-radius-sm) bg-surface-field focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
        selected
          ? 'size-(--mbm-size-version-thumb) ring-2 ring-surface-card'
          : 'size-(--mbm-size-version-thumb-small) opacity-(--mbm-opacity-pressed)',
      )}
    >
      {first ? <AssetImage asset={first} /> : null}
      <span className="absolute bottom-(--mbm-space-1) left-(--mbm-space-1) rounded-(--mbm-radius-xs) bg-overlay-scrim px-(--mbm-space-1) text-label-small text-text-on-dark">
        v{version.versionNumber}
      </span>
    </button>
  );
}

/**
 * The stage of the client's review page (Figma C05.2): the work itself on a sky, as it will look where it goes, and a
 * strip of the versions below it. What is drawn depends on the kind of work.
 */
export function ReviewStage({
  review,
  version,
  onSelect,
}: {
  review: ClientReview;
  version: ClientVersion;
  onSelect: (id: string) => void;
}) {
  const type = review.task.taskType;
  return (
    <SkyStage>
      {type === 'post' ? (
        <InstagramPreview review={review} version={version} />
      ) : type === 'reel' ? (
        <ReelPreview version={version} />
      ) : type === 'website' ? (
        <SitePreview version={version} />
      ) : (
        <FilesPreview assets={version.assets} />
      )}
      <div className="flex items-end justify-center gap-(--mbm-space-3)">
        {review.versions.map((v) => (
          <VersionThumb key={v.id} version={v} selected={v.id === version.id} onSelect={() => onSelect(v.id)} />
        ))}
      </div>
    </SkyStage>
  );
}

/**
 * A post's slides side by side on the dark stage (Figma C03.10): each 4:5, numbered, in the order they will swipe.
 * More than fit scroll sideways; the strip is centred when they don't.
 */
export function CarouselStrip({ assets }: { assets: BriefAsset[] }) {
  return (
    <ul
      aria-label="Slides"
      className="flex max-w-full items-center gap-(--mbm-space-3) overflow-x-auto px-(--mbm-space-6) py-(--mbm-space-4) [&>li:first-child]:ml-auto [&>li:last-child]:mr-auto"
    >
      {assets.map((asset, i) => (
        <li
          key={asset.id}
          className="relative aspect-[4/5] w-(--mbm-size-carousel-slide) shrink-0 overflow-hidden rounded-(--mbm-component-radius-slide) bg-surface-tint"
        >
          <AssetImage asset={asset} />
          <span className="absolute left-(--mbm-space-2) top-(--mbm-space-2) rounded-full bg-overlay-scrim px-(--mbm-space-1-5) text-label-tag text-text-on-dark">
            {i + 1}/{assets.length}
          </span>
          {asset.label ? (
            <span className="absolute bottom-(--mbm-space-2) left-(--mbm-space-2) right-(--mbm-space-2) truncate text-label-medium text-text-on-dark [text-shadow:0_1px_2px_rgb(0_0_0/0.5)]">
              {asset.label}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** Nothing to look at yet (Figma C03.10a): who is on it, how far along, when to expect it, and that nothing goes out unseen. */
export function WorkingStage({
  who,
  progress,
  expected,
}: {
  who: string;
  progress: number;
  /** "Wed 22 Oct", or null when no date is set. */
  expected: string | null;
}) {
  return (
    <div className="flex w-full max-w-(--mbm-layout-working-text-width) flex-col items-center gap-(--mbm-space-2-5) text-center">
      <h2 className="text-heading-h3 text-text-primary">{who} is working on it</h2>
      <p className="text-body-compact text-text-secondary">
        {progress > 0 ? `About ${progress}% done. ` : ''}
        {expected ? `First version expected ${expected}. ` : ''}
        Nothing goes live until you approve.
      </p>
      <ProgressBar
        value={progress}
        tone="brand"
        size="thick"
        track="strong"
        label={`${who}’s progress`}
        className="mt-(--mbm-space-2) w-(--mbm-layout-working-bar-width)"
      />
    </div>
  );
}
